import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Post } from '../../types/post';
import { getSightingType, getSightingTypeDisplay, getLocalizedPairingTypeLabel } from '../../types/post';
import type { Comment } from '../../types/comment';
import { useComments } from '../../hooks/useComments';
import { formatRelativeTime } from '../../utils/dateUtils';
import { TagDisplay } from '../tag/TagDisplay';
import { PostModerationActions } from '../moderation/PostModerationActions';
import { FlagPostButton } from './FlagPostButton';
import { DeletePostButton } from './DeletePostButton';
import { ShareButton } from '../share';
import { UserAvatar } from '../common/UserAvatar';
import { ProfilePictureModal } from '../common/ProfilePictureModal';
import { useLanguage } from '../../i18n/useLanguage';
import { useAuth } from '../../auth/useAuth';
import { Linkify } from '../common/Linkify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments } from '@fortawesome/free-solid-svg-icons';
import { MessageCaptionsIcon } from '../common/MessageCaptionsIcon';

interface PostCardProps {
  post: Post;
  commentCount?: number;
  showComments?: boolean;
  onRefreshList?: () => void;
  /** Timeline feed: green title, muted body, clock time in meta (day shown on rail). */
  layout?: 'default' | 'timeline';
}

export const PostCard: React.FC<PostCardProps> = ({
  post,
  showComments = false,
  onRefreshList,
  layout = 'default',
}) => {
  const { t, language } = useLanguage();
  const { isAuthenticated, user } = useAuth();
  const sightingLanguage = language === 'de' ? 'de' : 'en';
  const { comments, fetchComments } = useComments(post.id);
  const [displayComments, setDisplayComments] = useState<Comment[]>([]);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showCommentProfileModal, setShowCommentProfileModal] = useState<{ userId: string; username?: string } | null>(null);

  useEffect(() => {
    if (showComments && post.id) {
      fetchComments(post.id, 0, 3).catch((err) => {
        console.warn('Failed to fetch comments for post', post.id, err);
      });
    }
  }, [showComments, post.id, fetchComments]);

  useEffect(() => {
    if (showComments) {
      setDisplayComments(comments.slice(0, 3));
    }
  }, [comments, showComments]);

  return (
    <article
      className={`post-card ${layout === 'timeline' ? 'post-card--timeline' : ''} ${post.deleted ? 'post-card--deleted' : ''}`}
    >
      {post.deleted && (
        <div className="post-deleted-badge">
          <span>{t('post.deleted')}</span>
        </div>
      )}
      <div className="post-card-main">
        <Link to={`/posts/${post.id}`} className="post-card-link">
          {post.title && <h3 className={`post-card-title ${post.deleted ? 'post-card-title--deleted' : ''}`}>{post.title}</h3>}
          <p className={`post-card-content ${post.deleted ? 'post-card-content--deleted' : ''}`}><Linkify>{post.content}</Linkify></p>
          {(() => {
            const type = getSightingType(post);
            const display = getSightingTypeDisplay(type, sightingLanguage);
            if (!display) return null;
            return (
              <p className="post-card-sighting-type" data-sighting-type={type}>
                {display.emoji} {display.label}
              </p>
            );
          })()}
          {post.pairingType && (
            <p className="post-card-pairing-type" data-pairing-type={post.pairingType}>
              {getLocalizedPairingTypeLabel(post.pairingType, sightingLanguage)}
            </p>
          )}
          {post.tags && post.tags.length > 0 && <TagDisplay tags={post.tags} compact />}
          <div className="post-card-meta">
            {post.authorUsername && post.authorUsername.trim() && (
              <>
                <span className="post-card-by">{t('post.by')}</span>
                <span className="post-card-author">{post.authorUsername}</span>
                {post.authorId && (
                  <UserAvatar 
                    userId={post.authorId} 
                    username={post.authorUsername} 
                    size={24}
                    onClick={() => setShowProfileModal(true)}
                  />
                )}
              </>
            )}
            {post.deleted && <span className="post-card-deleted">({t('post.deleted').toLowerCase()})</span>}
          </div>
        </Link>
      </div>
      
      {showComments && displayComments.length > 0 && (
        <div className="post-card-comments">
          <div className="post-card-comments-header">
            <span className="post-card-comments-count">
              {comments.length}{' '}
              {comments.length === 1 ? t('post.comment') : t('post.comments')}
            </span>
          </div>
          <div className="post-card-comments-list">
            {displayComments.map((comment) => (
              <div key={comment.id} className="post-card-comment">
                <div className="post-card-comment-content">{comment.content}</div>
                <div className="post-card-comment-meta">
                  <span className="post-card-comment-time">{formatRelativeTime(comment.createdAt, language)}</span>
                  {comment.authorUsername && comment.authorUsername.trim() && (
                    <>
                      <span className="post-card-comment-by">{t('post.by')}</span>
                      <span className="post-card-comment-author">{comment.authorUsername}</span>
                      {comment.authorId && (
                        <UserAvatar 
                          userId={comment.authorId} 
                          username={comment.authorUsername} 
                          size={20}
                          onClick={() => setShowCommentProfileModal({ userId: comment.authorId, username: comment.authorUsername })}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
          {comments.length > 3 && (
            <Link to={`/posts/${post.id}#comment-form`} className="post-card-comments-more">
              {t('post.viewAllComments')} ({comments.length})
            </Link>
          )}
        </div>
      )}
      
      <div className="post-card-actions">
        <div className="post-card-actions-left">
          <Link to={`/posts/${post.id}#comment-form`} className="post-card-action-btn">
            <MessageCaptionsIcon className="post-card-action-icon" />
            <span>{t('post.actionComment')}</span>
          </Link>
          {isAuthenticated && post.authorId && user?.sub !== post.authorId && (
            <Link
              to={`/messages?userId=${encodeURIComponent(post.authorId)}&username=${encodeURIComponent(post.authorUsername || '')}`}
              className="post-card-action-btn"
            >
              <FontAwesomeIcon icon={faComments} className="post-card-action-icon" aria-hidden />
              <span>{t('post.actionContact')}</span>
            </Link>
          )}
          <ShareButton postId={post.id} postTitle={post.title} variant="text" label={t('post.actionShare')} className="post-card-action-btn" />
        </div>
        <div className="post-card-actions-right">
          {isAuthenticated && user?.sub === post.authorId ? (
            <DeletePostButton postId={post.id} authorId={post.authorId} variant="text" label={t('post.actionDelete')} onDeleted={onRefreshList} />
          ) : (
            post.authorId && post.flagStatus !== 'PENDING' && (
              <FlagPostButton postId={post.id} authorId={post.authorId} variant="text" label={t('post.actionReport')} onFlagged={onRefreshList} />
            )
          )}
        </div>
        <PostModerationActions
          postId={post.id}
          isDeleted={post.deleted}
          onDeleted={onRefreshList}
          onRestored={onRefreshList}
        />
      </div>
      
      {post.authorId && (
        <ProfilePictureModal
          isOpen={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          userId={post.authorId}
          username={post.authorUsername}
        />
      )}
      
      {showCommentProfileModal && (
        <ProfilePictureModal
          isOpen={!!showCommentProfileModal}
          onClose={() => setShowCommentProfileModal(null)}
          userId={showCommentProfileModal.userId}
          username={showCommentProfileModal.username}
        />
      )}
    </article>
  );
};

